import { useState, useEffect, useCallback } from 'react';
import {
  technicianAPI,
  Technician,
  CreateTechnicianData,
  UpdateTechnicianData
} from '@/lib/supabase/technicians';

export const useTechnicians = () => {
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zonesCache, setZonesCache] = useState<Map<string, string>>(new Map());

  // Helper function to enhance technician with zone names
  const enhanceTechnicianWithZones = useCallback(
    (technician: any) => {
      const assignedZoneIds = Array.isArray(technician.assigned_zone_ids)
        ? technician.assigned_zone_ids
        : technician.assigned_zone_ids
          ? [technician.assigned_zone_ids]
          : [];

      const assignedZoneNames = assignedZoneIds
        .map((zoneId: string) => zonesCache.get(zoneId))
        .filter((name: string | undefined) => name) as string[];

      return {
        ...technician,
        assigned_zone_ids: assignedZoneIds,
        assigned_zone_names: assignedZoneNames
      };
    },
    [zonesCache]
  );

  // Load technicians on mount
  useEffect(() => {
    loadTechnicians();
  }, []);

  const loadTechnicians = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get technicians with zone information
      const data = await technicianAPI.getTechniciansWithCampaigns();
      setTechnicians(data);

      // Cache zones for fast updates
      if (data.length > 0) {
        const zones = await technicianAPI.getZones();
        const zoneMap = new Map(zones.map((zone: any) => [zone.id, zone.name || zone.zone_name]));
        setZonesCache(zoneMap);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load technicians');
    } finally {
      setLoading(false);
    }
  }, []);

  const addTechnician = useCallback(
    async (technicianData: CreateTechnicianData) => {
      // Create optimistic technician with temporary ID
      const optimisticId = 'temp_' + Date.now();
      const optimisticTechnician = enhanceTechnicianWithZones({
        ...technicianData,
        id: optimisticId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });

      try {
        setError(null);

        // Immediately add optimistic technician to UI
        setTechnicians(prev => [optimisticTechnician, ...prev]);

        const newTechnician = await technicianAPI.createTechnician(technicianData);
        const enhancedTechnician = enhanceTechnicianWithZones(newTechnician);

        // Replace optimistic technician with real one
        setTechnicians(prev =>
          prev.map(tech => (tech.id === optimisticId ? enhancedTechnician : tech))
        );

        return enhancedTechnician;
      } catch (err) {
        // Remove optimistic technician on error
        setTechnicians(prev => prev.filter(tech => tech.id !== optimisticId));
        setError(err instanceof Error ? err.message : 'Failed to add technician');
        throw err;
      }
    },
    [enhanceTechnicianWithZones]
  );

  const updateTechnician = useCallback(
    async (id: string, updates: UpdateTechnicianData) => {
      // Store original technician for rollback
      let originalTechnician: Technician | undefined;

      try {
        setError(null);

        // Optimistic update: immediately update UI with zone names from existing technician
        // This ensures zone names are preserved during the update
        setTechnicians(prev =>
          prev.map(tech => {
            if (tech.id === id) {
              originalTechnician = tech;
              // Preserve existing zone names if assigned_zone_ids haven't changed
              const zoneIdsChanged = updates.assigned_zone_ids !== undefined;
              if (zoneIdsChanged) {
                // Zone IDs changed, enhance with new zone IDs using cache
                return enhanceTechnicianWithZones({ ...tech, ...updates });
              } else {
                // Zone IDs unchanged, just merge updates and preserve zone names
                return { ...tech, ...updates };
              }
            }
            return tech;
          })
        );

        // Perform the actual update
        const updatedTechnician = await technicianAPI.updateTechnician(id, updates);

        // Enhance with zone names from cache (fast operation)
        const enhancedTechnician = enhanceTechnicianWithZones(updatedTechnician);

        // Replace optimistic update with real data
        setTechnicians(prev => prev.map(tech => (tech.id === id ? enhancedTechnician : tech)));

        return enhancedTechnician;
      } catch (err) {
        // Rollback optimistic update on error
        if (originalTechnician) {
          setTechnicians(prev => prev.map(tech => (tech.id === id ? originalTechnician! : tech)));
        }
        setError(err instanceof Error ? err.message : 'Failed to update technician');
        throw err;
      }
    },
    [enhanceTechnicianWithZones]
  );

  const deleteTechnician = useCallback(async (id: string) => {
    // Store original technician for rollback
    let originalTechnician: Technician | undefined;
    let originalIndex: number = -1;

    try {
      setError(null);

      // Optimistic delete: immediately remove from UI
      setTechnicians(prev => {
        const newTechnicians = [...prev];
        originalIndex = newTechnicians.findIndex(tech => tech.id === id);
        if (originalIndex !== -1) {
          originalTechnician = newTechnicians[originalIndex];
          newTechnicians.splice(originalIndex, 1);
        }
        return newTechnicians;
      });

      await technicianAPI.deleteTechnician(id);
    } catch (err) {
      // Rollback optimistic delete on error
      if (originalTechnician && originalIndex !== -1) {
        setTechnicians(prev => {
          const newTechnicians = [...prev];
          newTechnicians.splice(originalIndex, 0, originalTechnician!);
          return newTechnicians;
        });
      }
      setError(err instanceof Error ? err.message : 'Failed to delete technician');
      throw err;
    }
  }, []);

  const getTechnician = useCallback(async (id: string) => {
    try {
      return await technicianAPI.getTechnician(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get technician');
      throw err;
    }
  }, []);

  const getTechniciansBySkillLevel = useCallback(async (skillLevel: number) => {
    try {
      return await technicianAPI.getTechniciansBySkillLevel(skillLevel);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get technicians by skill level');
      throw err;
    }
  }, []);

  // MSA Management Methods
  const getMSAs = useCallback(async () => {
    try {
      return await technicianAPI.getMSAs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get MSAs');
      throw err;
    }
  }, []);

  const getZipCodesByMSA = useCallback(async (msaId: string) => {
    try {
      return await technicianAPI.getZipCodesByMSA(msaId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get ZIP codes');
      throw err;
    }
  }, []);

  // Work Schedule Management Methods
  const getTechnicianWorkSchedules = useCallback(async (technicianId: string) => {
    try {
      return await technicianAPI.getTechnicianWorkSchedules(technicianId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get work schedules');
      throw err;
    }
  }, []);

  const saveTechnicianWorkSchedule = useCallback(
    async (technicianId: string, scheduleData: any) => {
      try {
        setError(null);

        const result = await technicianAPI.saveTechnicianWorkSchedule(technicianId, scheduleData);

        // Reload technicians to get updated schedule data
        await loadTechnicians();

        return result;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save work schedule');
        throw err;
      }
    },
    [loadTechnicians]
  );

  const checkTechnicianAvailability = useCallback(
    async (technicianId: string, date: string, time?: string) => {
      try {
        return await technicianAPI.checkTechnicianAvailability(technicianId, date, time);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to check availability');
        throw err;
      }
    },
    []
  );

  const getTechnicianWorkingHours = useCallback(async (technicianId: string, date: string) => {
    try {
      return await technicianAPI.getTechnicianWorkingHours(technicianId, date);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get working hours');
      throw err;
    }
  }, []);

  return {
    technicians,
    loading,
    error,
    addTechnician,
    updateTechnician,
    deleteTechnician,
    getTechnician,
    getTechniciansBySkillLevel,
    loadTechnicians,
    // MSA Management
    getMSAs,
    getZipCodesByMSA,
    // Work Schedule Management
    getTechnicianWorkSchedules,
    saveTechnicianWorkSchedule,
    checkTechnicianAvailability,
    getTechnicianWorkingHours
  };
};
